import { useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { createPortal } from "react-dom";

export default function PickPackReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [trackingOpen, setTrackingOpen] =
  useState(false);

const [trackingData, setTrackingData] =
  useState(null);

const [trackingLoading, setTrackingLoading] =
  useState(false);

  const [filter, setFilter] = useState({
    startDate: "",
    endDate: "",
  });

  const isFilterReady =
    filter.startDate && filter.endDate;

  // =========================
  // FETCH DATA
  // =========================
  const fetchData = async () => {
    try {
      if (!isFilterReady) return;

      setLoading(true);

      const kode_toko =
        localStorage.getItem("kode_toko");

      const res = await axios.get(
        "/api/tiktokshop/pickpack-report",
        {
          params: {
            start_date: filter.startDate,
            end_date: filter.endDate,
            kode_toko,
          },
        }
      );

      setData(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

const fetchTrackingDetail = async (
  waybillId,
  courierCode
) => {
  try {
    setTrackingOpen(true);
    setTrackingLoading(true);

    const API_KEY =
      "biteship_live.eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJuYW1lIjoidHJhY2UiLCJ1c2VySWQiOiI2NWZkYWVjNzBiZGMwODAwMTI5YTM2NGIiLCJpYXQiOjE3Nzk2NTQyNjN9.Eamias94ap6Xdhc7sjqsG_4mGEbh46caxuhKRg-CcKU";

    const res = await axios.get(
      `https://api.biteship.com/v1/trackings/${waybillId}/couriers/jnt`,
      {
        headers: {
          Authorization: API_KEY,
        },
      }
    );

    setTrackingData(res.data || null);
  } catch (err) {
    console.error(err);
  } finally {
    setTrackingLoading(false);
  }
};

  // =========================
  // SEARCH FILTER
  // =========================
  const filteredData = useMemo(() => {
    if (!search) return data;

    return data.filter((d) =>
      `
      ${d.kode_extend}
      ${d.order_id}
      ${d.seller_sku}
      ${d.tracking_number}
      ${d.nama_toko}
      ${d.biensi_stat}
      ${d.status}
      `
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [data, search]);

  // =========================
  // SUMMARY
  // =========================
  const summary = useMemo(() => {
    return {
      total_order: new Set(
        data.map((d) => d.order_id)
      ).size,

      total_artikel: new Set(
        data.map((d) => d.kode_extend)
      ).size,

      total_qty: data.reduce(
        (a, b) => a + Number(b.qty || 0),
        0
      ),

      total_revenue: data.reduce(
        (a, b) => a + Number(b.revenue || 0),
        0
      ),
    };
  }, [data]);

  // =========================
  // GROUPING SKU
  // =========================
  const groupedSku = useMemo(() => {
    const map = {};

    data.forEach((item) => {
      const key = item.kode_extend;

      if (!map[key]) {
        map[key] = {
          artikel: item.kode_extend,
          seller_sku: item.seller_sku,
          qty: 0,
          revenue: 0,
          hjp: 0,
        };
      }

      map[key].qty += Number(item.qty || 0);

      map[key].revenue += Number(
        item.revenue || 0
      );

      map[key].hjp += Number(item.hjp || 0);
    });

    return Object.values(map);
  }, [data]);

  // =========================
  // EXPORT EXCEL
  // =========================
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    // ================= RAW DATA =================
    const rawData = data.map((d) => ({
      STORE: d.nama_toko,
      STATUS: d.status,

      ORDER_DATE: d.order_date
        ? new Date(d.order_date)
            .toISOString()
            .replace("T", " ")
            .replace("Z", "")
        : "-",

      PICK_DATE: d.pick_date
        ? new Date(d.pick_date)
            .toISOString()
            .replace("T", " ")
            .replace("Z", "")
        : "-",

      ORDER_ID: d.order_id,

      ARTIKEL: d.kode_extend,

      SELLER_SKU: d.seller_sku,

      SKU_ID: d.sku_id,

      WAREHOUSE: d.warhouse_pick,
      BIENSI_STATUS: d.biensi_stat,

      HANDLING: d.handling_method,

      COURIER: d.shipping_provider_name,

      TRACKING: d.tracking_number || "-",

      PAYMENT_ID: d.id_pembayaran,

      QTY: Number(d.qty || 0),

      HJP: Number(d.hjp || 0),

      REVENUE: Number(d.revenue || 0),

      
    }));

    const wsRaw =
      XLSX.utils.json_to_sheet(rawData);

    wsRaw["!cols"] = [
      { wch: 28 },
      { wch: 22 },
      { wch: 22 },
      { wch: 22 },
      { wch: 24 },
      { wch: 20 },
      { wch: 20 },
       { wch: 18 },
      { wch: 24 },
      { wch: 24 },
      { wch: 22 },
      { wch: 18 },
      { wch: 22 },
      { wch: 20 },
      { wch: 10 },
      { wch: 14 },
      { wch: 16 },
     
    ];

    // ================= GROUPING =================
    const groupingSheet = groupedSku.map(
      (d) => ({
        ARTIKEL: d.artikel,
        SELLER_SKU: d.seller_sku,
        TOTAL_QTY: d.qty,
        TOTAL_HJP: d.hjp,
        TOTAL_REVENUE: d.revenue,
      })
    );

    const wsGroup =
      XLSX.utils.json_to_sheet(groupingSheet);

    wsGroup["!cols"] = [
      { wch: 24 },
      { wch: 24 },
      { wch: 14 },
      { wch: 18 },
      { wch: 18 },
    ];

    // ================= SUMMARY =================
    const summarySheet = [
      {
        METRIC: "TOTAL ORDER",
        VALUE: summary.total_order,
      },
      {
        METRIC: "TOTAL ARTIKEL",
        VALUE: summary.total_artikel,
      },
      {
        METRIC: "TOTAL QTY",
        VALUE: summary.total_qty,
      },
      {
        METRIC: "TOTAL REVENUE",
        VALUE: summary.total_revenue,
      },
    ];

    const wsSummary =
      XLSX.utils.json_to_sheet(summarySheet);

    wsSummary["!cols"] = [
      { wch: 25 },
      { wch: 20 },
    ];

    // ================= APPEND =================
    XLSX.utils.book_append_sheet(
      wb,
      wsSummary,
      "Summary"
    );

    XLSX.utils.book_append_sheet(
      wb,
      wsGroup,
      "Grouping SKU"
    );

    XLSX.utils.book_append_sheet(
      wb,
      wsRaw,
      "Raw Data"
    );

    XLSX.writeFile(
      wb,
      `pickpack-report-${filter.startDate}-${filter.endDate}.xlsx`
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen space-y-5">

      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Pickpack Order Report
        </h1>

        <p className="text-sm text-gray-500 mt-1">
          TikTok Shop Pickpack Monitoring
        </p>
      </div>

      {/* FILTER */}
      <div className="bg-white border rounded-2xl p-4 flex flex-wrap gap-3 items-end shadow-sm">

        <div>
          <label className="text-xs text-gray-500 block mb-1">
            Start Date
          </label>

          <input
            type="date"
            className="border rounded-lg px-3 py-2 text-sm"
            value={filter.startDate}
            onChange={(e) =>
              setFilter({
                ...filter,
                startDate: e.target.value,
              })
            }
          />
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">
            End Date
          </label>

          <input
            type="date"
            className="border rounded-lg px-3 py-2 text-sm"
            value={filter.endDate}
            onChange={(e) =>
              setFilter({
                ...filter,
                endDate: e.target.value,
              })
            }
          />
        </div>

        <button
          onClick={fetchData}
          disabled={!isFilterReady}
          className={`px-5 py-2 rounded-lg text-sm text-white transition
          ${
            isFilterReady
              ? "bg-black hover:bg-gray-800"
              : "bg-gray-300"
          }
          `}
        >
          Load Data
        </button>

        <input
          className="ml-auto border px-3 py-2 rounded-lg text-sm w-80"
          placeholder="Search artikel / order / tracking..."
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
        />

        <button
          onClick={exportExcel}
          disabled={!data.length}
          className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition"
        >
          Export Excel
        </button>
      </div>

      {/* SUMMARY */}
      {isFilterReady && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <div className="text-xs text-gray-500">
              Total Order
            </div>

            <div className="text-2xl font-bold mt-1">
              {summary.total_order}
            </div>
          </div>

          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <div className="text-xs text-gray-500">
              Total Artikel
            </div>

            <div className="text-2xl font-bold mt-1">
              {summary.total_artikel}
            </div>
          </div>

          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <div className="text-xs text-gray-500">
              Total Qty
            </div>

            <div className="text-2xl font-bold mt-1">
              {summary.total_qty}
            </div>
          </div>

          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <div className="text-xs text-gray-500">
              Total Revenue
            </div>

            <div className="text-2xl font-bold mt-1 text-green-600">
              Rp{" "}
              {summary.total_revenue.toLocaleString(
                "id-ID"
              )}
            </div>
          </div>

        </div>
      )}

      {/* TABLE */}
      {isFilterReady && (
        <div className="bg-white border rounded-2xl overflow-hidden shadow-sm">

          <div className="p-4 border-b flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-800">
                Pickpack Order Datatable
              </h2>

              <p className="text-xs text-gray-500">
                Total Data :{" "}
                {filteredData.length}
              </p>
            </div>
          </div>

          <div className="overflow-auto max-h-[700px]">

            <table className="w-full text-sm">

              <thead className="bg-gray-100 sticky top-0 z-10 text-gray-700">
                <tr>

                  <th className="p-3 text-left">
                    Photo
                  </th>

                  <th className="p-3 text-left">
                    Order Date
                  </th>

                    <th className="p-3 text-left">
                    Order ID
                  </th>

                  <th className="p-3 text-left">
                    Store
                  </th>
<th className="p-3 text-left">
                    Warehouse
                  </th>
                  <th className="p-3 text-left">
  Shipping
</th>
                  <th className="p-3 text-left">
                    Status
                  </th>

                

                  <th className="p-3 text-left">
                    Artikel
                  </th>

                  <th className="p-3 text-left">
                    Seller SKU
                  </th>

                  

                 

                  <th className="p-3 text-center">
                    Qty
                  </th>

                  <th className="p-3 text-right">
                    HJP
                  </th>

                  <th className="p-3 text-right">
                    Revenue
                  </th>

                </tr>
              </thead>

              <tbody>

                {loading ? (
                  <tr>
                    <td
                      colSpan={13}
                      className="text-center p-10 text-gray-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length ? (

                  filteredData.map((d, i) => (

                    <tr
                      key={i}
                      className="border-t hover:bg-gray-50 transition"
                    >

                      {/* PHOTO */}
                      <td className="p-3">
                        <img
                          src={d.sku_image}
                          alt=""
                          className="w-14 h-14 rounded-lg object-cover border"
                        />
                      </td>

                      {/* ORDER DATE */}
                      <td className="p-3 whitespace-nowrap">
                        {d.order_date
                          ? new Date(
                              d.order_date
                            ).toLocaleString(
                              "id-ID"
                            )
                          : "-"}
                      </td>

                         {/* ORDER ID */}
                      <td className="p-3 font-medium ">
                        {d.order_id}
                      </td>


                      {/* STORE */}
                      <td className="p-3 font-medium text-gray-700">
                        {d.nama_toko}
                      </td>

                      <td className="p-3">
  <div className="text-xs space-y-1">

    {/* WAREHOUSE */}
    <div className="font-medium text-gray-800">
      {d.warhouse_pick}
    </div>

    {/* BIENSI STATUS */}
    <div className="flex items-center gap-1 text-gray-500">

      {d.biensi_stat === "MUTATION" ? (
        <>
          {/* ICON UP DOWN */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 7l4-4m0 0l4 4m-4-4v18M16 17l-4 4m0 0l-4-4m4 4V3"
            />
          </svg>

          <span>MUTATION</span>
        </>
      ) : (
        <>
          {/* ICON DOWN */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>

          <span>NON MUTATION</span>
        </>
      )}

    </div>

  </div>
</td>

{/* SHIPPING */}
<td className="p-3">
  <button
    onClick={() =>
      fetchTrackingDetail(
        d.tracking_number,
        d.shipping_provider_name
      )
    }
    className="space-y-1 text-left hover:bg-gray-50 p-2 rounded-xl transition w-full"
  >

    <div className="flex items-center gap-2">

      <img
        src="data:image/png;base64,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"
        alt="Courier"
        className="w-8 h-8 object-contain rounded"
      />

      <div>
        <div className="font-medium text-gray-800">
          {d.shipping_provider_name}
        </div>

        <div className="text-xs font-medium text-gray-500">
          {d.tracking_number || "-"}
        </div>
      </div>

    </div>
  </button>
</td>
                      {/* STATUS */}
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold
                          ${
                            d.status ===
                            "AWAITING_COLLECTION"
                              ? "bg-yellow-100 text-yellow-700"
                              : d.status ===
                                "CANCELLED" || d.status === "CANCEL ADJUSTED"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-700"
                          }
                        `}
                        >
                          {d.status}
                        </span>
                      </td>

                   
                      {/* ARTIKEL */}
                      <td className="p-3 font-semibold text-blue-600">
                        {d.kode_extend}
                      </td>

                      {/* SELLER SKU */}
                      <td className="p-3">
                        {d.seller_sku}
                      </td>

                     

               

                      {/* QTY */}
                      <td className="p-3 text-center font-semibold">
                        {d.qty}
                      </td>

                      {/* HJP */}
                      <td className="p-3 text-right font-medium">
                        Rp{" "}
                        {Number(
                          d.hjp || 0
                        ).toLocaleString(
                          "id-ID"
                        )}
                      </td>

                      {/* REVENUE */}
                      <td className="p-3 text-right font-bold text-green-600">
                        Rp{" "}
                        {Number(
                          d.revenue || 0
                        ).toLocaleString(
                          "id-ID"
                        )}
                      </td>

                    </tr>
                  ))

                ) : (
                  <tr>
                    <td
                      colSpan={13}
                      className="text-center p-10 text-gray-400"
                    >
                      Tidak ada data
                    </td>
                  </tr>
                )}

              </tbody>

            </table>

          </div>

        </div>
      )}

  {trackingOpen &&
  createPortal(
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[2147483647]">
      
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
        
        {/* LOADING / CONTENT */}
        {!trackingData ? (
          <div className="p-10 text-center text-gray-500">
            {trackingLoading ? "Loading tracking..." : "No tracking data"}
          </div>
        ) : (
          <>
            {/* HEADER */}
            <div className="border-b p-5 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-start justify-between">
                
                <div className="flex items-center gap-3">
                  <img
                    src="data:image/png;base64,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"
        className="w-10 h-10 rounded-xl border bg-white p-1"
                    alt="courier"
                  />

                  <div>
                    <h2 className="text-xl font-bold">
                      {trackingData.waybill_id}
                    </h2>
                    <div className="text-sm text-gray-500 uppercase">
                      {trackingData.courier?.company}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setTrackingOpen(false);
                    setTrackingData(null);
                  }}
                  className="w-10 h-10 rounded-full hover:bg-gray-100 transition"
                >
                  ✕
                </button>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <div className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold uppercase">
                  {trackingData.status}
                </div>

                <div className="text-xs text-gray-500">
                  {trackingData.history?.length || 0} Tracking History
                </div>
              </div>
            </div>

            {/* BODY */}
            <div className="overflow-auto max-h-[70vh]">
              
              {/* ORIGIN / DEST */}
              

              {/* TIMELINE */}
              <div className="p-6 space-y-6">
                {trackingData.history?.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      {index !== trackingData.history.length - 1 && (
                        <div className="w-[2px] flex-1 bg-gray-200" />
                      )}
                    </div>

                    <div className="flex-1 pb-6">
                      <div className="flex justify-between">
                        <div className="text-sm font-semibold text-gray-800">
                          {item.note}
                        </div>
                        <div className="text-[10px] px-2 py-1 bg-gray-100 rounded text-gray-600">
                          {item.status}
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 mt-2">
                        {new Date(item.updated_at).toLocaleString("id-ID")}
                      </div>
                    </div>

                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

    </div>,
    document.body
  )
}


    </div>

    

    
  );

}